if (!customElements.get("video-reels")) {
    customElements.define(
        "video-reels",
        class VideoReels extends HTMLElement {
            constructor() {
                super();
                this.activeClass = "active";
                this.swiper = this.querySelector(".wt-slider__container");

                this.youtubePlayers = new WeakMap();
                this.vimeoPlayers = new WeakMap();

                this.isSectionVisible = false;
            }

            connectedCallback() {
                this._sectionObserver = null;
                this.init();
            }

            disconnectedCallback() {
                if (this._sectionObserver) {
                    this._sectionObserver.disconnect();
                    this._sectionObserver = null;
                }

                if (this.checkInterval) clearInterval(this.checkInterval);
            }

            getMediaController(slide) {
                const video = slide.querySelector("video");

                if (video) {
                    return {
                        type: "native",

                        play: () => video.play().catch(() => {}),

                        pause: () => video.pause(),

                        mute: () => {
                            video.muted = true;
                        },

                        unmute: () => {
                            video.muted = false;
                        },
                    };
                }

                const youtube = slide.querySelector(".js-youtube");

                if (youtube) {
                    const playerPromise = this.getYouTubePlayer(youtube);

                    return {
                        type: "youtube",

                        play: () => {
                            playerPromise?.then((player) => {
                                player.playVideo?.();
                            });
                        },

                        pause: () => {
                            playerPromise?.then((player) => {
                                player.pauseVideo?.();
                            });
                        },

                        mute: () => {
                            playerPromise?.then((player) => {
                                player.mute?.();
                            });
                        },

                        unmute: () => {
                            playerPromise?.then((player) => {
                                player.unMute?.();
                            });
                        },
                    };
                }

                const vimeo = slide.querySelector(".js-vimeo");

                if (vimeo) {
                    const player = this.getVimeoPlayer(vimeo);

                    return {
                        type: "vimeo",

                        play: () => player?.play().catch(() => {}),

                        pause: () => player?.pause().catch(() => {}),

                        mute: () => player?.setMuted(true),

                        unmute: () => player?.setMuted(false),
                    };
                }

                return null;
            }

            getYouTubePlayer(iframe) {
                if (!window.YT?.Player) return null;

                if (!this.youtubePlayers.has(iframe)) {
                    const playerPromise = new Promise((resolve) => {
                        new window.YT.Player(iframe, {
                            events: {
                                onReady: (event) => {
                                    resolve(event.target);
                                },
                            },
                        });
                    });

                    this.youtubePlayers.set(iframe, playerPromise);
                }

                return this.youtubePlayers.get(iframe);
            }

            getVimeoPlayer(iframe) {
                if (!window.Vimeo?.Player) return null;

                if (!this.vimeoPlayers.has(iframe)) {
                    const player = new Vimeo.Player(iframe);

                    this.vimeoPlayers.set(iframe, player);
                }

                return this.vimeoPlayers.get(iframe);
            }

            observeSection(swiper) {
                const observer = new IntersectionObserver(
            ([entry]) => {
                        this.isSectionVisible = entry.isIntersecting;

                        const activeSlide = this.findActiveSlide(swiper);
                        const player = this.getMediaController(activeSlide);

                        if (!player) return;

                        if (entry.isIntersecting) {
                            this.playVideoInActiveSlide(swiper);
                        } else {
                            player.pause();
                        }
                    },
            {
                        threshold: 0.1,
                    },
                );

                observer.observe(this);
                this._sectionObserver = observer;
            }

            hardStopAllExceptActive(swiper) {
                const activeSlide = swiper.slides[swiper.activeIndex];

                swiper.slides.forEach((slide) => {
                    if (slide === activeSlide) return;

                    const player = this.getMediaController(slide);

                    if (!player) return;

                    player.pause();
                    player.mute();
                });
            }

            handleSoundToggle(swiper) {
                swiper.slides.forEach((slide) => {
                    const button = slide.querySelector(".wt-video__sound-toggle");

                    if (!button || button._bound) return;

                    button._bound = true;

                    button.addEventListener("click", () => {
                        const activeSlide = this.findActiveSlide(swiper);
                        const player = this.getMediaController(activeSlide);

                        if (!player) return;

                        this.hardStopAllExceptActive(swiper);

                        const soundOn = swiper.el.dataset.sound === "on";
                        const nextSoundOn = !soundOn;

                        swiper.el.dataset.sound = nextSoundOn ? "on" : "off";

                        if (nextSoundOn) {
                            player.unmute();
                        } else {
                            player.mute();
                        }

                        player.play();
                    });
                });
            }

            sanitizeVideosOnce() {
                if (this._sanitized) return;
                this._sanitized = true;
                this.querySelectorAll("video").forEach((video) => {
                    video.pause();
                    video.muted = true;
                    video.removeAttribute("autoplay");
                    video.autoplay = false;
                    video.setAttribute("playsinline", "");
                    video.setAttribute("webkit-playsinline", "");
                    video.preload = "metadata";
                });
            }

            playVideoInActiveSlide(swiper) {
                if (!this.isSectionVisible) return;

                const activeSlide = this.findActiveSlide(swiper);
                const player = this.getMediaController(activeSlide);

                if (!player) return;

                const soundOn = swiper.el.dataset.sound === "on";

                if (soundOn) {
                    player.unmute();
                } else {
                    player.mute();
                }

                player.play();
            }

            findActiveSlide(swiper) {
                const activeSlide = swiper.slides[swiper.activeIndex];
                return activeSlide;
            }

            toggleActiveClass(swiper) {
                const activeSlide = this.findActiveSlide(swiper);
                swiper.slides.forEach((slide) => {
                    slide.classList.remove(this.activeClass);

                    if (activeSlide === slide) {
                        slide.classList.add(this.activeClass);
                    }
                });
            }

            handleSlideChange(swiper) {
                this.hardStopAllExceptActive(swiper);
                this.toggleActiveClass(swiper);
                this.playVideoInActiveSlide(swiper);
            }

            addVideoEventHandlers(swiper) {
                this._onSlideChange = () => this.handleSlideChange(swiper);
                swiper.on("slideChange", this._onSlideChange);
                swiper.on("slidesLengthChange", () => this.handleSoundToggle(swiper));
                swiper.on("update", () => this.handleSoundToggle(swiper));
            }

            checkSwiperInitialization() {
                const swiperContainer = this.swiper;
                const mySwiperInstance = swiperContainer?.swiper;

                if (swiperContainer?.classList.contains("swiper-initialized") && mySwiperInstance) {
                    if (!this.swiper?.dataset.sound) {
                        this.swiper.dataset.sound = "off";
                    }

                    this.sanitizeVideosOnce();
                    this.addVideoEventHandlers(mySwiperInstance);
                    this.handleSoundToggle(mySwiperInstance);
                    this.toggleActiveClass(mySwiperInstance);
                    this.hardStopAllExceptActive(mySwiperInstance);
                    this.observeSection(mySwiperInstance);
                    clearInterval(this.checkInterval);
                }
            }

            init() {
                this.checkInterval = setInterval(this.checkSwiperInitialization.bind(this), 250);
            }
        },
    );
}
